import React, { useState, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import dayjs from "dayjs"
import { Box, Stack } from "@mui/material"

import DataGrid from "../../components/DataGrid/DataGrid"
import GridThumbnail from "../../components/DataGrid/GridThumbnail"
import { getPackagingListSelector } from "../../reducers/Packaging/packagingList"
import { packagingNature, packagingTypes, packagingCategories } from "../../utils/packaging"
import { ACTIVE_KFC_BRANDS, KFC_BRANDS, getBrandBy } from "../../utils"
import { getOrganizationsEnterprisesSelector } from "../../reducers/Organizations/organizations"
import { generateCloudinaryDownloadUrl } from "../../utils/cloudinary"
import { createOrUpdatePackaging, showPackaging } from "../../actions/Packaging/packaging"
import { loadEnterprises } from "../../actions/Organizations/Enterprise"
import PackagingFormDialog from "./PackagingFormDialog"
import FixedAddButton from "../../components/FixedAddButton"

const enterpriseLogoUrl = (e) =>
	e.logo?.publicId ? generateCloudinaryDownloadUrl(e.logo.publicId) : null

const enterpriseCellIcon = (enterprise, index) => {
	const url = enterpriseLogoUrl(enterprise)
	if (url) return <GridThumbnail key={index} src={url} alt={enterprise.name} />
	return (
		<Box
			key={index}
			title={enterprise.name}
			sx={{
				width: 25, height: 25, borderRadius: "50%",
				bgcolor: enterprise.color || "#9B9B9B",
				display: "inline-flex", alignItems: "center", justifyContent: "center",
				fontSize: 10, fontWeight: 700, color: "#fff", mr: "5px", flexShrink: 0,
			}}
		>
			{enterprise.name?.charAt(0).toUpperCase()}
		</Box>
	)
}

const enterpriseDropdownIcon = (enterprise) => {
	const url = enterpriseLogoUrl(enterprise)
	return url
		? <img src={url} alt={enterprise.name} style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "contain" }} />
		: <span style={{ width: 20, height: 20, borderRadius: "50%", background: enterprise.color || "#9B9B9B", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{enterprise.name?.charAt(0).toUpperCase()}</span>
}

const Packagings = () => {
	const [data, setData] = useState([])
	const [openFormDialog, setOpenFormDialog] = useState(false)

	const packagings = useSelector(getPackagingListSelector)
	const enterprises = useSelector(getOrganizationsEnterprisesSelector).filter(e => e.status !== "ARCHIVED")

	const dispatch = useDispatch()

	useEffect(() => {
		if (!enterprises.length) dispatch(loadEnterprises())
	}, [])

	const columns = useMemo(() => [
		{
			key: "id",
			label: "ID",
			dataType: "text",
			sortable: false,
			filter: { filterable: true, type: "text", strict: true },
			width: 120
		},
		{
			key: "name",
			label: "Nom",
			dataType: "text",
			sortable: false,
			filter: { filterable: true, type: "text", fullText: true }
		},
		{
			key: "reference",
			label: "Réference",
			dataType: "text",
			sortable: false,
			filter: { filterable: true, type: "text", fullText: true }
		},
		{
			key: "type",
			label: "Type",
			dataType: "text",
			sortable: false,
			filter: {
				filterable: true,
				type: "select",
				options: packagingTypes.map((packagingType) => ({ key: packagingType.value, value: packagingType.label, label: packagingType.label }))
			}
		},
		{
			key: "nature",
			label: "Nature",
			dataType: "text",
			sortable: false,
			filter: {
				filterable: true,
				type: "select",
				options: packagingNature.map((n) => ({ key: n.value, value: n.label, label: n.label }))
			}
		},
		{
			key: "category",
			label: "Catégorie",
			dataType: "text",
			sortable: false,
			filter: {
				filterable: true,
				type: "select",
				options: packagingCategories.map((c) => ({ key: c.value, value: c.label, label: c.label }))
			}
		},
		{
			key: "brands",
			label: "Marque / Entreprise",
			dataType: "text",
			sortable: false,
			filter: {
				filterable: true,
				type: "select",
				options: [
					{ type: "subheader", label: "Marques" },
					...ACTIVE_KFC_BRANDS.map(brand => ({
						key: brand.name,
						value: brand.name,
						label: brand.label,
						icon: brand.image ? <img src={brand.image} alt={brand.label} style={{ width: 20, height: 20, objectFit: "contain" }} /> : null,
					})),
					{ type: "subheader", label: "Entreprises" },
					...enterprises.map(e => ({
						key: e.objectId,
						value: e.objectId,
						label: e.name,
						icon: enterpriseDropdownIcon(e),
					})),
				],
				render: (value) => {
					const brand = getBrandBy("name", value)
					if (brand) return brand.label
					return enterprises.find(e => e.objectId === value)?.name ?? value
				}
			},
			render: (brandsStr) => (
				<Stack direction="row" flexWrap="wrap">
					{brandsStr.split(",").filter(Boolean).map((value, index) => {
						const brand = KFC_BRANDS.find(b => b.name === value)
						if (brand) return <GridThumbnail key={index} src={brand.image} alt={`${brand.shortname}_logo`} />
						const enterprise = enterprises.find(e => e.objectId === value)
						return enterprise ? enterpriseCellIcon(enterprise, index) : null
					})}
				</Stack>
			),
			width: 220
		},
		{
			key: "updatedAt",
			label: "Date de modification",
			dataType: "text",
			sortable: true,
			filter: { filterable: false }
		}
	], [enterprises])

	useEffect(() => {
		const newPackagings = packagings.map((packaging) => ({
			id: packaging.id,
			name: packaging.get("name"),
			reference: packaging.get("reference"),
			type: packagingTypes.find(p => p.value === packaging.get("type"))?.label,
			nature: packagingNature.find(p => p.value === packaging.get("nature"))?.label,
			category: packagingCategories.find(p => p.value === packaging.get("categories"))?.label,
			brands: [
				...(packaging.get("brands") || []),
				...(packaging.toJSON().enterprises || []).map(e => e.objectId),
			].join(","),
			updatedAt: dayjs(packaging.updatedAt).format("DD/MM/YYYY"),
		}))
		setData(newPackagings)
	}, [packagings])

	const toggleCreate = () => {
		setOpenFormDialog(!openFormDialog)
	}
	const onShowPackaging = (packaging) => {
		dispatch(showPackaging(packaging.id))
	}

	const handleCreatePackaging = (values) => {
		dispatch(createOrUpdatePackaging(values, null, true))
		toggleCreate()
	}

	return (
		<>
			<DataGrid
				title="Packagings"
				columns={columns}
				data={data}
				onRowClick={onShowPackaging}
				withFilters
			/>
			<FixedAddButton color="primary" onClick={toggleCreate} sx={{ left: "initial" }} />

			{/* creation */}
			<PackagingFormDialog
				onClose={toggleCreate}
				open={openFormDialog}
				onSave={handleCreatePackaging}
			/>
		</>
	)
}

export default Packagings